import { classNames } from '@panwds/react-ui'
import { createElement, forwardRef } from 'react'

import type { IconDefinition } from '@fortawesome/pro-solid-svg-icons'
import type { ComponentPropsWithRef, CSSProperties, ReactElement } from 'react'

export type { IconDefinition }

const sizeVariants = {
  xxs: 'tw-h-2 tw-w-2',
  xs: 'tw-h-3 tw-w-3',
  sm: 'tw-h-3.5 tw-w-3.5',
  md: 'tw-h-4 tw-w-4',
  lg: 'tw-h-5 tw-w-5',
  xl: 'tw-h-6 tw-w-6',
  xxl: 'tw-h-7 tw-w-7',
  '3xl': 'tw-h-8 tw-w-8',
  '4xl': 'tw-h-10 tw-w-10',
  '5xl': 'tw-h-12 tw-w-12',
}
export interface FaIconProps
  extends Omit<ComponentPropsWithRef<'svg'>, 'className'> {
  /**
   * Adds additional class name(s) to the svg.
   */
  addClassName?: string
  /**
   * A label for the icon. If no label is provided, the icon will be `aria-hidden`.
   */
  'aria-label'?: string
  /**
   * Specifies the color of the icon.
   */
  color?: string
  /**
   * The [Font Awesome](https://fontawesome.com/icons?d=gallery) icon defintion object.
   */
  faIconDef?: IconDefinition
  secondaryColor?: string
  style?: CSSProperties
  size?: keyof typeof sizeVariants
  /**
   * In case faIconDef is not provided then as a fall back it will look for
   * fontIconClass
   * If this is provided then it will render in <i/> element with provided font icon class name.
   */
  fontIconClass?: string
  /**
   * If fontIconClass is also not provided then as another fall back it will look for IconElement
   * If we want to render custom icon then we can provide the Name of that icon commponent.
   * and it will simply render that component.
   */
  IconElement?: ReactElement
  /**
   * many times we might want to provide some additional props to custom IconElement
   * So we can mention all of them inside this prop and then the will be made available to IconComponent
   */
  iconProps?: IconElementProps
  /**
   * for styling f given IconElement we can provide stylings through iconprops
   */
  imgSrc?: string
  /**
   * In case custom IconElement is also not provided then as a final fall back will check if
   * text prop is passed
   * In case it is available then it will simply sender this text inside a span tag
   * If we want to style this text then we can pass the call name in the addClassName props.
   */
  text?: string
}

export interface IconElementProps {
  fontSize?: string
  color?: string
}

const defaultProps = {
  addClassName: '',
  color: 'currentColor',
  secondaryColor: 'currentColor',
  iconProps: {},
}

function getIconPathData(iconDef: IconDefinition): string[] {
  const path = iconDef.icon[4]
  if (Array.isArray(path)) {
    return path
  }
  return [path]
}

function getViewBox(iconDef: IconDefinition): string {
  const width = iconDef.icon[0]
  const height = iconDef.icon[1]

  return `0 0 ${width} ${height}`
}

function setIconElement(
  IconElement: ReactElement,
  iconProps?: IconElementProps
): ReactElement {
  const style = { ...IconElement.props.style, ...iconProps }
  const className = IconElement.props.className
  const Element = createElement(IconElement.type, { style, className })
  return Element
}

/**
 * Renders Font Awesome icons.
 *
 * [Check out the Font Awesome icon gallery.](https://fontawesome.com/icons?d=gallery)
 *
 * To use, import the icon definition from the appropriate font awesome library:
 *
 * ```jsx
 * import { faAbacus, faFire } from "@fortawesome/pro-solid-svg-icons";
 * ```
 *
 * And pass the definition to the `FaIcon` component:
 *
 * ```jsx
 * <FaIcon faIconDef={faFire} />
 * ```
 *
 * If the icon has semantic meaning, use the `aria-label` attribute to make it accessible to all users. If no
 * `aria-label` attribute is provided, it will be assumed that the icon is purely for decoration and it will
 * be `aria-hidden`.
 *
 * ### Import Guide
 *
 * ```jsx
 * import { FaIcon } from "@panwds/react-ui";
 * ```
 */
export const FaIcon = forwardRef<SVGSVGElement, FaIconProps>(
  (props: FaIconProps, ref) => {
    const {
      // eslint-disable-next-line react/prop-types
      addClassName,
      // eslint-disable-next-line react/prop-types
      color,
      // eslint-disable-next-line react/prop-types
      faIconDef,
      // eslint-disable-next-line react/prop-types
      IconElement,
      // eslint-disable-next-line react/prop-types
      iconProps,
      // eslint-disable-next-line react/prop-types
      text,
      // eslint-disable-next-line react/prop-types
      fontIconClass,
      // eslint-disable-next-line react/prop-types
      secondaryColor,
      // eslint-disable-next-line react/prop-types
      size = 'md',
      // eslint-disable-next-line react/prop-types
      imgSrc,
      ...svgProps
    } = props

    const a11yProps = svgProps['aria-label'] ?
      {} :
      { 'aria-hidden': true, focusable: false }

    const classes = classNames(
      'tw-inline-block tw-overflow-visible tw-align-middle',
      sizeVariants[size],
      addClassName
    )
    return faIconDef ? (
      <svg
        {...a11yProps}
        role="img"
        viewBox={getViewBox(faIconDef)}
        {...svgProps}
        className={classes}
        ref={ref}
      >
        {getIconPathData(faIconDef).map((path, index) => (
          <path
            fill={index === 0 ? color : secondaryColor}
            d={path}
            key={index}
          />
        ))}
      </svg>
    ) : fontIconClass ? (
      <i className={`tw-inline-block tw-overflow-visible ${fontIconClass}`} />
    ) : IconElement ? (
      <>{setIconElement(IconElement, iconProps)}</>
    ) : text ? (
      <span className={addClassName}>{text}</span>
    ) : (
      // eslint-disable-next-line jsx-a11y/alt-text
      <img className={classes} src={imgSrc} />
    )
  }
)

FaIcon.displayName = 'FaIcon'
FaIcon.defaultProps = defaultProps
